// feature/role-views — abogado's "Mis Casos" priority dashboard (SPEC §9.5).
// feature/phase2-kickoff — segmented by fecha_ingreso into Hoy / Ayer / Esta semana / Todos / Por fecha.
// feature/lawyer-tabs. Wrapped in a top-level Mis casos /
// Historial tab strip; stat cards are now clickable and open a drilldown
// modal listing the matching cases.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cases as casesApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import Spinner from "../../components/Spinner";
import Empty from "../../components/Empty";
import CenteredModal from "../../components/CenteredModal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ContestadaTag, slaState } from "../../components/Pills";
import EstadoFilterPills, { filterByActiveStatus } from "../../components/EstadoFilterPills";
import AbogadoCaseDetail from "./AbogadoCaseDetail";
import HistorialTab from "./HistorialTab";
import { useRefreshOnNotification } from "../../components/useRefreshOnNotification";
import { useContextMenu } from "../../components/ContextMenu";
import { useHoverIntent } from "../../hooks/useHoverIntent";
import { HoverFocusProvider, useHoverFocus } from "../../context/HoverFocusContext";

const PRIORITY_META = {
  1: { color: "var(--color-danger)",  label: "Tratamiento médico" },
  2: { color: "var(--color-warning)", label: "N/A puro" },
  3: { color: "var(--accent-mint)",   label: "Insumo recibido" },
  4: { color: "var(--border-strong)", label: null },
};

const DIAS_ES   = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES_ES  = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

// Bogotá calendar parts for a given Date (or YYYY-MM-DD string treated as Bogotá-local).
function bogotaParts(d) {
  const f = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota",
    year: "numeric", month: "2-digit", day: "2-digit", weekday: "short",
  });
  const parts = Object.fromEntries(f.formatToParts(d).map(p => [p.type, p.value]));
  return { y: Number(parts.year), m: Number(parts.month), d: Number(parts.day) };
}
function bogotaDateStr(d) {
  const p = bogotaParts(d);
  return `${p.y}-${String(p.m).padStart(2,"0")}-${String(p.d).padStart(2,"0")}`;
}
function todayBogotaStr() { return bogotaDateStr(new Date()); }

// Format YYYY-MM-DD (interpreted as Bogotá-local) as "lunes, 20 de mayo de 2026".
function formatBogotaDateLong(yyyyMmDd) {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return "";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  // Use a Date at noon Bogotá (UTC-5 = 17:00 UTC) to avoid any DST/edge weirdness when reading weekday.
  const dt = new Date(Date.UTC(y, m - 1, d, 17, 0, 0));
  const weekday = new Intl.DateTimeFormat("en-US", { timeZone: "America/Bogota", weekday: "long" }).format(dt).toLowerCase();
  // Map English weekday → Spanish (DIAS_ES indexed by JS getDay = sun..sat)
  const wdIdx = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"].indexOf(weekday);
  return `${DIAS_ES[wdIdx]}, ${d} de ${MESES_ES[m - 1]} de ${y}`;
}

function daysOpen(c) {
  return Math.floor((Date.now() - new Date(c.fecha_ingreso).getTime()) / 86400000);
}

const TAB_META = [
  { key: "today",     label: "Hoy" },
  { key: "yesterday", label: "Ayer" },
  { key: "this_week", label: "Esta semana" },
  { key: "all",       label: "Todos" },
  { key: "by_date",   label: "Por fecha" },
];

// feature/lawyer-tabs. Top-level shell switches between
// the live "Mis casos" priority dashboard and the new "Historial" tab.
// Tab state lives here so switching tabs doesn't unmount the inner
// providers — but we don't try to preserve filter/period state across
// tabs (each tab is independent).
export default function MisCasos() {
  const [topTab, setTopTab] = useState("mis_casos");
  return (
    <div className="col" style={{ gap: 16 }}>
      <TopTabs active={topTab} onChange={setTopTab} />
      {topTab === "mis_casos" && (
        <HoverFocusProvider>
          <MisCasosInner />
        </HoverFocusProvider>
      )}
      {topTab === "historial" && <HistorialTab />}
    </div>
  );
}

function TopTabs({ active, onChange }) {
  return (
    <div role="tablist" style={{
      display: "flex", gap: 4, flexWrap: "wrap",
      borderBottom: "1px solid var(--border-hairline)",
      marginTop: 4,
    }}>
      <TopTabButton label="Mis casos" icon="📋"
                    active={active === "mis_casos"}
                    onClick={() => onChange("mis_casos")} />
      <TopTabButton label="Historial" icon="🕘"
                    active={active === "historial"}
                    onClick={() => onChange("historial")} />
    </div>
  );
}

function TopTabButton({ label, icon, active, onClick }) {
  return (
    <button type="button" role="tab" aria-selected={active}
            onClick={onClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 16px",
              background: "transparent", border: "none",
              borderBottom: `2px solid ${active ? "var(--accent-mint)" : "transparent"}`,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--accent-mint)" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: -1,
            }}>
      <span aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function MisCasosInner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { focusedRowId } = useHoverFocus();
  const [activeTab, setActiveTab] = useState("today");
  const [pickedDate, setPickedDate] = useState(todayBogotaStr());
  const [byDateCount, setByDateCount] = useState(0);
  // feature/estado-filter-pills. Default to active so closed
  // cases tuck behind a click. Per-page state, no localStorage.
  const [estadoFilter, setEstadoFilter] = useState("activos");
  // feature/insumos-listos-tile — URL-driven orthogonal filter.
  // When ?filter=insumos_listos lands on the page, pill arrives active
  // and the items fetch passes it through to the backend.
  const insumosListosActive = searchParams.get("filter") === "insumos_listos";
  // sprint83 — URL-driven LITISOFT-backlog filter, mirrors insumos_listos.
  const litisoftBacklogActive = searchParams.get("filter") === "litisoft_backlog";

  // All assigned cases (for stat cards) — fetched once per refresh tick, period=all.
  const [allData, setAllData] = useState(null);
  // Period-filtered items (for the active tab's list).
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({ today: 0, yesterday: 0, this_week: 0, all: 0 });
  const [openId, setOpenId] = useState(null);
  // Sprint 58 — clicking a stat card opens a drilldown modal listing the
  // cases that contributed to that metric. null = closed.
  const [drilldown, setDrilldown] = useState(null);

  // feature/fallos-por-revisar-tile — team-wide pending-review count.
  // Backend emits the field only for users with revisor_fallos (e.g.
  // Molly). null = field absent → no tile.
  const [fallosCount, setFallosCount] = useState(null);

  // feature/insumos-listos-tile — abogado-scoped "tu insumo llegó" count.
  // Backend emits only for users with abogado role. null = field absent
  // → no tile.
  const [insumosListosCount, setInsumosListosCount] = useState(null);

  // feature/litisoft-mandatory-fields-v1 — sprint83. Abogado-scoped
  // count of active assigned cases with any of the 3 mandatory LITISOFT
  // fields empty. Backend emits only for users with abogado role.
  // Click activates ?filter=litisoft_backlog (orthogonal to period
  // tabs, same pattern as insumos_listos).
  const [litisoftBacklogCount, setLitisoftBacklogCount] = useState(null);

  function setInsumosListosFilter(on) {
    const next = new URLSearchParams(searchParams);
    if (on) next.set("filter", "insumos_listos");
    else    next.delete("filter");
    setSearchParams(next, { replace: false });
  }
  function setLitisoftBacklogFilter(on) {
    const next = new URLSearchParams(searchParams);
    if (on) next.set("filter", "litisoft_backlog");
    else    next.delete("filter");
    setSearchParams(next, { replace: false });
  }

  // Stats source: full set, regardless of active tab.
  function loadAll() {
    setAllData(null);
    casesApi.myPriority({ period: "all" })
      .then(r => {
        setAllData(r.data.items || []);
        setCounts(r.data.counts || { today: 0, yesterday: 0, this_week: 0, all: 0 });
        // feature/fallos-por-revisar-tile — field is omitted (not 0) when
        // the user lacks revisor_fallos. Honor that semantics here.
        setFallosCount(Object.prototype.hasOwnProperty.call(r.data, "fallos_por_revisar_count")
          ? r.data.fallos_por_revisar_count : null);
        // feature/insumos-listos-tile — same omit-or-include pattern, abogado-gated.
        setInsumosListosCount(Object.prototype.hasOwnProperty.call(r.data, "insumos_listos_count")
          ? r.data.insumos_listos_count : null);
        // sprint83 — litisoft backlog tile, abogado-gated.
        setLitisoftBacklogCount(Object.prototype.hasOwnProperty.call(r.data, "litisoft_backlog_count")
          ? r.data.litisoft_backlog_count : null);
      })
      .catch(() => { setAllData([]); });
  }

  // feature/insumos-listos-tile — hotfix (sprint77 regression). The
  // insumos_listos filter is semantically a slice of "Todos"; it must NOT
  // compose with period tabs (Hoy/Ayer/Esta semana/Por fecha) or it
  // silently zeros out every period view for abogados with 0 insumos
  // listos. URL param is preserved so toggling back to Todos restores the
  // filter; the pill is hidden + the backend filter param is suppressed
  // while on a period tab.
  const insumosListosEffective = insumosListosActive && activeTab === "all";
  // sprint83 — same period-tab guard as insumos_listos: backlog filter
  // is a slice of Todos, must not compose with period buckets.
  const litisoftBacklogEffective = litisoftBacklogActive && activeTab === "all";

  // Per-tab fetch.
  function loadItems() {
    setItems(null);
    const params = activeTab === "by_date"
      ? { date: pickedDate }
      : { period: activeTab };
    if (insumosListosEffective)    params.filter = "insumos_listos";
    if (litisoftBacklogEffective)  params.filter = "litisoft_backlog";
    casesApi.myPriority(params)
      .then(r => {
        setItems(r.data.items || []);
        if (r.data.counts) setCounts(r.data.counts);
        if (activeTab === "by_date") setByDateCount((r.data.items || []).length);
      })
      .catch(() => setItems([]));
  }

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadItems(); /* eslint-disable-next-line */ }, [activeTab, pickedDate, insumosListosEffective, litisoftBacklogEffective]);

  // feature/smart-refresh — soft refresh when relevant notifications land.
  useRefreshOnNotification(
    ["case_assigned","audit_approved","audit_rejected","case.devuelto_resuelto","insumo_response"],
    () => { loadAll(); loadItems(); }
  );

  // 60s polling: refresh both stats source and active list.
  useEffect(() => {
    const t = setInterval(() => { loadAll(); loadItems(); }, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [activeTab, pickedDate]);

  const stats = useMemo(() => {
    const all = allData || [];
    const open = all.filter(c => c.estado !== "cerrado");
    let urgent = 0, breached = 0, audit = 0;
    for (const c of open) {
      const s = slaState(c);
      if (s?.kind === "warn") urgent++;
      if (s?.kind === "bad")  breached++;
      if (c.estado === "en_auditoria") audit++;
    }
    return { activos: open.length, urgent, breached, audit };
  }, [allData]);

  const firstName = (user.name || "").split(" ")[0];

  function tabBadgeCount(key) {
    if (key === "by_date") return byDateCount;
    return counts[key] ?? 0;
  }

  function emptyMessage() {
    if (insumosListosActive) {
      return {
        title: "No tiene insumos listos para contestar",
        message: "Cuando el equipo le deje un caso listo, aparecerá aquí.",
      };
    }
    switch (activeTab) {
      case "today":     return { title: "No tiene casos nuevos hoy" };
      case "yesterday": return { title: "No recibió casos ayer" };
      case "this_week": return { title: "No tiene casos de esta semana" };
      case "all":       return { title: "No tiene casos asignados", message: "Cuando Reparto le asigne un caso, aparecerá aquí ordenado por prioridad." };
      case "by_date":   return { title: "No recibió casos ese día" };
      default:          return { title: "Sin resultados" };
    }
  }

  function refreshAfterChange() {
    loadAll();
    loadItems();
  }

  return (
    <div className="col" style={{ gap: 24 }}>
      <div>
        <h1 style={{ margin: 0 }}>Mis casos</h1>
        <div className="muted">Hola, {firstName} — {stats.activos} activo(s)</div>
      </div>

      <div className={"grid-" + (4 + (fallosCount !== null ? 1 : 0) + (insumosListosCount !== null ? 1 : 0) + (litisoftBacklogCount !== null ? 1 : 0))}>
        <Stat label="Activos hoy"   value={stats.activos}
              onClick={() => setDrilldown("activos")} />
        <Stat label="Por vencer"    value={stats.urgent}
              tone={stats.urgent ? "warning" : null}
              onClick={() => setDrilldown("urgent")} />
        <Stat label="Vencidos"      value={stats.breached}
              tone={stats.breached ? "danger" : null}
              onClick={() => setDrilldown("breached")} />
        {/* feature/fallos-por-revisar-tile — only visible to revisor_fallos
            (backend gates the field). Always renders the number (0 stays
            visible — the role implies the responsibility). Click jumps to
            /fallos so Molly doesn't lose her second queue between tabs. */}
        {fallosCount !== null && (
          <Stat label="Fallos por revisar" value={fallosCount}
                tone={fallosCount ? "info" : null}
                onClick={() => navigate("/fallos")} />
        )}
        {/* feature/insumos-listos-tile — abogado-gated. Click activates the
            ?filter=insumos_listos URL param so the abogado lands on a
            filtered list of cases ready to contestar. 0 stays visible. */}
        {insumosListosCount !== null && (
          <Stat label="Insumos listos" value={insumosListosCount}
                tone={insumosListosCount ? "info" : null}
                onClick={() => { setActiveTab("all"); setInsumosListosFilter(true); }} />
        )}
        {/* sprint83 — click activates ?filter=litisoft_backlog (same
            pattern as Insumos listos: switches to the Todos tab and
            slices to the cases with one of the 3 mandatory fields empty). */}
        {litisoftBacklogCount !== null && (
          <Stat label="Pendientes LITISOFT" value={litisoftBacklogCount}
                tone={litisoftBacklogCount ? "warning" : null}
                onClick={() => { setActiveTab("all"); setLitisoftBacklogFilter(true); }} />
        )}
        <Stat label="En auditoría"  value={stats.audit}
              tone={stats.audit ? "info" : null}
              onClick={() => setDrilldown("audit")} />
      </div>

      <DrilldownModal
        kind={drilldown}
        allData={allData || []}
        onClose={() => setDrilldown(null)}
        onOpenCase={(id) => { setDrilldown(null); setOpenId(id); }}
      />

      <div className="row" style={{ gap: 4, borderBottom: "1px solid var(--border-hairline)" }}>
        {TAB_META.map(t => (
          <Tab
            key={t.key}
            label={t.label}
            count={tabBadgeCount(t.key)}
            active={activeTab === t.key}
            onClick={() => setActiveTab(t.key)}
          />
        ))}
      </div>

      {activeTab === "by_date" && (
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="date"
            value={pickedDate}
            onChange={(e) => setPickedDate(e.target.value || todayBogotaStr())}
            style={{ width: "auto" }}
          />
          {pickedDate !== todayBogotaStr() && (
            <button className="ghost sm" onClick={() => setPickedDate(todayBogotaStr())}>Volver a hoy</button>
          )}
          <div className="muted" style={{ marginLeft: "auto", fontSize: 13 }}>
            Casos del {formatBogotaDateLong(pickedDate)}
          </div>
        </div>
      )}

      {/* feature/estado-filter-pills. Sits below the
          period tabs so the period sets the dataset and these pills
          slice it by active/closed.
          feature/insumos-listos-tile — pill rendered as a sibling on the
          same row when the user has abogado role (backend gates the count). */}
      {items != null && items.length > 0 && (
        <div className="row" style={{ gap: 16, flexWrap: "wrap", alignItems: "center" }}>
          <EstadoFilterPills cases={items} value={estadoFilter} onChange={setEstadoFilter} />
          {/* feature/insumos-listos-tile — pill visible only on the Todos
              tab. Period tabs slice by intake date already; layering the
              insumos_listos filter on top zeros out everyone with 0
              insumos listos. Hotfix for sprint77 regression. */}
          {insumosListosCount !== null && activeTab === "all" && (
            <button type="button"
                    onClick={() => setInsumosListosFilter(!insumosListosActive)}
                    aria-pressed={insumosListosActive}
                    className={"badge " + (insumosListosActive ? "info" : "muted")}
                    style={{
                      cursor: "pointer",
                      padding: "4px 12px",
                      fontSize: 12,
                      border: insumosListosActive
                        ? "1px solid var(--color-info)"
                        : "1px solid var(--border-hairline)",
                      background: insumosListosActive ? "var(--accent-mint-bg)" : "transparent",
                    }}>
              Insumos listos ({insumosListosCount})
            </button>
          )}
          {/* sprint83 — mirrors the insumos_listos pill: only on Todos tab. */}
          {litisoftBacklogCount !== null && activeTab === "all" && (
            <button type="button"
                    onClick={() => setLitisoftBacklogFilter(!litisoftBacklogActive)}
                    aria-pressed={litisoftBacklogActive}
                    className={"badge " + (litisoftBacklogActive ? "warning" : "muted")}
                    style={{
                      cursor: "pointer",
                      padding: "4px 12px",
                      fontSize: 12,
                      border: litisoftBacklogActive
                        ? "1px solid var(--color-warning)"
                        : "1px solid var(--border-hairline)",
                      background: litisoftBacklogActive ? "var(--color-warning-bg)" : "transparent",
                    }}>
              Pendientes LITISOFT ({litisoftBacklogCount})
            </button>
          )}
        </div>
      )}

      {items == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : !items.length ? (
        <div className="card"><Empty {...emptyMessage()} /></div>
      ) : (() => {
        let filtered = filterByActiveStatus(items, estadoFilter);
        // Sprint X5-fix — within the Contestados bucket, sort by
        // contestada_at DESC so the most recently contestada case is on
        // top. Matches lawyer expectation when reviewing recent work.
        // Other buckets keep the natural order (priority_score from
        // myPriority + fecha_ingreso ASC tiebreak applied by the
        // backend already).
        if (estadoFilter === "contestados") {
          filtered = [...filtered].sort((a, b) => {
            const ta = a.contestada_at ? new Date(a.contestada_at).getTime() : 0;
            const tb = b.contestada_at ? new Date(b.contestada_at).getTime() : 0;
            return tb - ta;
          });
        }
        if (!filtered.length) {
          return <div className="card"><Empty title={
            estadoFilter === "cerrados"    ? "Sin casos cerrados en este periodo"
              : estadoFilter === "contestados" ? "Sin contestadas en este periodo"
              : estadoFilter === "activos"   ? "Sin casos activos en este periodo"
              : emptyMessage().title
          } /></div>;
        }
        return (
          <div className={"col eds-focus-list" + (focusedRowId ? " has-focused-row" : "")} style={{ gap: 8 }}>
            {filtered.map(c => {
              const meta = PRIORITY_META[c.priority_score] || PRIORITY_META[4];
              return (
                <CaseRow key={c.id} c={c} meta={meta} onOpen={() => setOpenId(c.id)} />
              );
            })}
          </div>
        );
      })()}

      <AbogadoCaseDetail caseId={openId} open={!!openId} onClose={() => setOpenId(null)} onChanged={refreshAfterChange} />
    </div>
  );
}

// feature/notion-polish — extracted row with right-click context menu support.
// feature/apple-treatment — wired into shared hover-focus state.
function CaseRow({ c, meta, onOpen }) {
  const { onContextMenu, menu } = useContextMenu([
    { label: "Abrir caso",      icon: "↗",  onClick: onOpen },
    { label: "Copiar enlace",   icon: "🔗", onClick: () => {
      const url = `${window.location.origin}/cases?open=${c.id}`;
      navigator.clipboard?.writeText(url).catch(() => {});
    }},
  ]);
  const { setFocusedRowId } = useHoverFocus();
  const intent = useHoverIntent(300, 100);
  useEffect(() => {
    if (intent.isFocused) setFocusedRowId(c.id);
    else setFocusedRowId(prev => (prev === c.id ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent.isFocused, c.id]);
  return (
    <>
      <div
        className={"card clickable eds-focus-row" + (intent.isFocused ? " is-focused" : "")}
        onClick={onOpen}
        onContextMenu={onContextMenu}
        onMouseEnter={intent.onMouseEnter}
        onMouseLeave={intent.onMouseLeave}
        style={{
          padding: 16,
          display: "flex", alignItems: "center", gap: 16,
          borderLeft: `4px solid ${meta.color}`,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <strong style={{ color: "var(--text-primary)" }}>{c.case_number}</strong>
            <TipoPill tipo={c.tipo} />
            {c.tipo === "fallo" && <TipoFalloPill value={c.tipo_fallo} />}
            {meta.label && (
              <span className="badge" style={{ background: meta.color + "22", color: meta.color }}>
                {meta.label}
              </span>
            )}
            {c.late_entry && !c.late_entry_approved && (
              <span className="badge warning">Extemporáneo pendiente</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-body)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>{c.accionante_nombre || "—"} · <span className="muted">{c.juzgado || "—"}</span></span>
            {c.radicado && (
              <span className="muted" style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 11 }}>
                · rad. {c.radicado}
              </span>
            )}
            {/* feature/contestada-tag. */}
            <ContestadaTag contestadaAt={c.contestada_at} estado={c.estado} dateFormat="short" />
          </div>
        </div>
        <div style={{ textAlign: "right", display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
          <SlaPill c={c} />
          <EstadoPill c={c} />
          <div className="muted" style={{ fontSize: 11 }}>{daysOpen(c)} día(s) abierto</div>
        </div>
      </div>
      {menu}
    </>
  );
}

function Tab({ label, count, active, onClick }) {
  return (
    <div onClick={onClick} style={{
      padding: "10px 14px", cursor: "pointer", fontSize: 13,
      color: active ? "var(--accent-mint)" : "var(--text-muted)",
      borderBottom: "2px solid " + (active ? "var(--accent-mint)" : "transparent"),
      fontWeight: active ? 500 : 400, marginBottom: -1,
    }}>
      {label} <span className="muted" style={{ fontSize: 12 }}>({count})</span>
    </div>
  );
}

function Stat({ label, value, tone, onClick }) {
  const color = tone === "danger"  ? "var(--color-danger)"
              : tone === "warning" ? "var(--color-warning)"
              : tone === "info"    ? "var(--color-info)"
              : "var(--text-primary)";
  // Sprint 58 — stat cards are interactive: clicking opens a drilldown
  // modal listing the cases. Hover lifts the card to signal that.
  return (
    <div className="card tight clickable"
         role={onClick ? "button" : undefined}
         tabIndex={onClick ? 0 : undefined}
         onClick={onClick}
         onKeyDown={(e) => { if (onClick && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onClick(); } }}
         style={onClick ? { cursor: "pointer" } : undefined}>
      <div className="label-uc">{label}</div>
      <div style={{ fontSize: 32, fontWeight: 600, color, marginTop: 6 }}>{value}</div>
      {onClick && (
        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>Clic para ver casos</div>
      )}
    </div>
  );
}

// Sprint 58 — drilldown modal. Pulls from `allData` (the full priority
// list) and applies the same predicate that produced the stat. Lists
// rows with a click-to-open hand-off back to the parent.
const DRILLDOWN_META = {
  activos:  { title: "Casos activos", subtitle: "Todos los casos asignados que no están cerrados.",
              predicate: (c) => c.estado !== "cerrado" },
  urgent:   { title: "Casos por vencer", subtitle: "SLA en zona amarilla — actuar pronto.",
              predicate: (c) => slaState(c)?.kind === "warn" && c.estado !== "cerrado" },
  breached: { title: "Casos vencidos", subtitle: "SLA en zona roja — fuera de plazo.",
              predicate: (c) => slaState(c)?.kind === "bad" && c.estado !== "cerrado" },
  audit:    { title: "Casos en auditoría", subtitle: "En revisión por dirección antes del cierre.",
              predicate: (c) => c.estado === "en_auditoria" },
};

function DrilldownModal({ kind, allData, onClose, onOpenCase }) {
  const meta = kind ? DRILLDOWN_META[kind] : null;
  const matched = useMemo(() => {
    if (!meta) return [];
    return allData.filter(meta.predicate);
  }, [allData, meta]);
  return (
    <CenteredModal
      open={!!meta}
      onClose={onClose}
      title={meta?.title || ""}
      subtitle={meta ? `${matched.length} caso${matched.length === 1 ? "" : "s"} · ${meta.subtitle}` : ""}
      size="lg"
    >
      {matched.length === 0 ? (
        <Empty title="Nada por aquí" message="No hay casos en esta categoría." />
      ) : (
        <div className="col" style={{ gap: 8 }}>
          {matched.map((c) => (
            <DrilldownRow key={c.id} c={c} onClick={() => onOpenCase(c.id)} />
          ))}
        </div>
      )}
    </CenteredModal>
  );
}

function DrilldownRow({ c, onClick }) {
  return (
    <div className="card clickable"
         onClick={onClick}
         style={{ padding: 12, display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <strong>{c.case_number}</strong>
          <TipoPill tipo={c.tipo} />
          {c.tipo === "fallo" && <TipoFalloPill value={c.tipo_fallo} />}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-body)" }}>
          {c.accionante_nombre || "—"} · <span className="muted">{c.juzgado || "—"}</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
        <SlaPill c={c} />
        <EstadoPill c={c} />
      </div>
    </div>
  );
}
